import React from 'react';
import {
  AP_COMPUTER_SCIENCE_PRINCIPLES,
  AP_CALCULUS_AB,
  AP_CALCULUS_BC,
  AP_MACROECONOMICS,
  AP_MICROECONOMICS,
  AP_CHINESE,
  AP_JAPANESE,
  AP_ITALIAN,
  AP_FRENCH,
  AP_GERMAN,
  AP_SPANISH,
  AP_ENGLISH_LIT,
  AP_SPANISH_LIT,
  AP_LATIN,
} from 'chameleon-src/constants/subjects';
import { OPENED } from './constants';
import { AssessmentProgress } from '../AssessmentProgress';
import { ADMIN_UNIT_OUTLINE as COPY } from 'chameleon/ui-stack/utilities/copy';
import { SubunitAssessmentTypes } from 'legacy-packages/unit-outline/src/constants';

const CALCULUS_SUB = [AP_CALCULUS_AB, AP_CALCULUS_BC];
const WORLD_LANGUAGE_SUBJECT_MAP = [
  AP_FRENCH,
  AP_GERMAN,
  AP_SPANISH,
  AP_ITALIAN,
  AP_CHINESE,
  AP_JAPANESE,
];
const NO_SKILL_FILTER_SUBJECT_MAP = [AP_MACROECONOMICS, AP_MICROECONOMICS];

const SUBJECTS_WITH_UNIT_DESCRIPTION = [
  AP_ENGLISH_LIT,
  AP_SPANISH_LIT,
  AP_LATIN,
  ...WORLD_LANGUAGE_SUBJECT_MAP,
];

/**
 * Return an hierarchical labels in array like format
 * of a metadata object.
 * from the Spreadsheet a hierarchy looks like this
 *    [Framework Category, Level1, Level2, Level3, Level...n]
 *    ['Practice and Skill', 'Practice', 'Skill']
 *    [Skill and Learning Objective, Skill Category, Skill]
 **/
const getMetadataObjectHirerachyLabels = (metadataObj) => {
  const stack = new Set();
  const getChildrenMeta = (children) => {
    children.forEach((child) => {
      stack.add(child.category);
      if (child.type === 'hierarchy' && child.children) {
        getChildrenMeta(child.children);
      }
    });
  };
  // for the root parent use the label
  if (metadataObj.children && metadataObj.type === 'hierarchy') {
    stack.add(metadataObj.label);
    getChildrenMeta(metadataObj.children);
  }
  return Array.from(stack);
};

const getTopicQuestionsFilter = ({
  subjectId,
  unitNumber,
  metadata,
  widgetTagColumn,
  skipCustomFilters = false,
  showMainTQFiltersOnly = false,
  unitTitle,
  unitsLabel,
  subunitLabel = null,
}) => {
  if (!metadata) return;
  let metadataClone = showMainTQFiltersOnly ? [] : JSON.parse(metadata);
  const intSubjectId = Number.parseInt(subjectId, 10);

  // for now hardcode the assessmentPurposeSource and AB/BC Domain filter on the front-end
  const assessmentPurposeSource = {
    name: 'Assessment Purpose and Source',
    label: 'Assessment Purpose and Source',
    category: null,
    type: 'hierarchy',
    children: [
      {
        category: 'Assessment Type',
        label: 'Formative',
        name: 'Formative',
        type: 'hierarchy',
        children: [
          {
            category: 'Assessment Type',
            label: 'AP Topic Question',
            name: 'AP Topic Question',
            type: 'hierarchy',
            children: [],
          },
        ],
      },
    ],
  };

  /**
   * Ensure to add the Performance Task tag filter for CSP
   */
  if (intSubjectId === AP_COMPUTER_SCIENCE_PRINCIPLES) {
    assessmentPurposeSource.children[0].children.push({
      category: 'Assessment Type',
      label: 'Performance Task',
      name: 'Performance Task',
      type: 'hierarchy',
      children: [],
    });
  }

  const unitFilter = {
    type: 'flat',
    name: unitsLabel || 'Unit',
    values: [
      {
        label: unitNumber,
        title: unitNumber,
        name: SUBJECTS_WITH_UNIT_DESCRIPTION.includes(subjectId)
          ? `${unitNumber}: ${unitTitle}`
          : unitNumber,
      },
    ],
  };

  if (!showMainTQFiltersOnly && CALCULUS_SUB.includes(subjectId)) {
    const createDomainTagFilter = (val) => ({
      name: 'AB/BC Domain',
      type: 'flat',
      values: val.map((v) => ({ label: v, title: v, name: v })),
    });
    const domainFilterTag =
      subjectId === AP_CALCULUS_AB
        ? createDomainTagFilter(['AB/BC'])
        : createDomainTagFilter(['AB/BC', 'BC Only']);
    metadataClone.push(domainFilterTag);
  }

  if (
    !showMainTQFiltersOnly &&
    (WORLD_LANGUAGE_SUBJECT_MAP.includes(intSubjectId) ||
      intSubjectId === AP_COMPUTER_SCIENCE_PRINCIPLES ||
      subunitLabel === 'Topics')
  ) {
    // remove skills filter for world languages subjects
    // Unit + Task Model + Assessment Source and Purpose
    const cols = widgetTagColumn.split('>');
    const skillColumn = cols[cols.length - 1];
    metadataClone = metadataClone.filter((meta) => {
      const metaHierarchies = getMetadataObjectHirerachyLabels(meta);
      let metaRootCategory = '';
      if (metaHierarchies.includes(skillColumn)) {
        metaRootCategory = metaHierarchies[0];
      }
      return meta.label !== metaRootCategory;
    });
  }

  if (SUBJECTS_WITH_UNIT_DESCRIPTION.includes(subjectId)) {
    metadataClone.push(unitFilter);
  }

  if (
    !showMainTQFiltersOnly &&
    NO_SKILL_FILTER_SUBJECT_MAP.includes(subjectId)
  ) {
    const skillFilterIndex = metadataClone.findIndex(
      (x) => x.label === 'Skill Category and Skill',
    );

    if (skillFilterIndex >= 0) {
      metadataClone.splice(skillFilterIndex, 1);
    }
  }

  // See https://academicmerit.atlassian.net/browse/FY-8329 for unit handling
  if (widgetTagColumn && widgetTagColumn.indexOf('Skill Category') >= 0) {
    metadataClone.push(unitFilter);
  }

  if (!skipCustomFilters || showMainTQFiltersOnly) {
    metadataClone.push(assessmentPurposeSource);
  }

  return metadataClone;
};

const detectUnlockedStatus = (
  assignmentsForResource,
  assessmentId,
  isAdminOrCoordinator,
  assessmentProgressComponent,
  isPPC,
  isPreApSubject,
) => {
  // for admins/coord show an unlocked state for the first mcq/frq ppc
  if (
    isAdminOrCoordinator &&
    assessmentProgressComponent &&
    (isPPC || isPreApSubject)
  ) {
    return true;
  }

  return (
    assessmentId &&
    assignmentsForResource.some(({ status }) => status === OPENED)
  );
};

const detectAvailableProgress = (
  isStudent,
  assignmentsForResource,
  assessmentId,
) => {
  const oneUnlocked = assignmentsForResource.some(
    ({ teacherAssigned }) => !!teacherAssigned,
  );
  return (
    assessmentId && !isStudent && assignmentsForResource.length && oneUnlocked
  );
};

const renderAssessmentProgress = (
  isProgressAvailable,
  assignmentsForResource,
  displayName,
  resource,
  isAdminOrCoordinator,
  subjectId,
  isPPC,
  isPreApSubject,
  rawAssignments,
  scope,
  subunitType = SubunitAssessmentTypes.PPC,
  disablePpcAutoAssignFlag = false,
  unitNumber = '',
  unitTitle = '',
  assessmentId,
) => {
  let showProgressIcon = isProgressAvailable;
  let assignments = assignmentsForResource;
  let ppcType;
  let adminMessage;

  // FY-6874 use fake assignments progress and micon
  if (isAdminOrCoordinator && isPPC) {
    const isFRQ = displayName.includes('FRQ');

    if (isFRQ) {
      showProgressIcon = true;
      ppcType = subunitType === SubunitAssessmentTypes.PE ? 'FRQ_PE' : `FRQ_1`;
      adminMessage =
        subunitType === SubunitAssessmentTypes.PE || disablePpcAutoAssignFlag
          ? COPY.PE_FRQ
          : COPY.FRQ_ALERT;
    }
  }

  return showProgressIcon ? (
    <AssessmentProgress
      assignments={assignments}
      subjectId={subjectId}
      isAdminOrCoordinator={isAdminOrCoordinator}
      ppcType={ppcType}
      adminMessage={adminMessage}
      rawAssignments={rawAssignments || []}
      scope={scope}
      subunitType={subunitType}
      displayName={displayName}
      disablePpcAutoAssignFlag={disablePpcAutoAssignFlag}
      unitNumber={unitNumber}
      unitTitle={unitTitle}
      assessmentId={assessmentId}
      isPPC={isPPC}
    />
  ) : null;
};

const getAssignmentsForResource = ({
  assignments = [],
  resource,
  updatedAssignments,
}) =>
  assignments
    .filter(
      (assignment) => assignment.assessment.itemId === resource.assessmentId,
    )
    .map((assignment) => {
      const updatedAssignment = updatedAssignments.find(
        (a) => a.id && a.id.toString() === assignment.id,
      );

      let result = assignment;
      if (updatedAssignment) {
        const {
          opened,
          starts_at: startsAt,
          status,
          teacher_assigned: teacherAssigned,
        } = updatedAssignment;
        result = {
          ...result,
          opened,
          startsAt,
          status,
          teacherAssigned,
        };
      }

      return result;
    })
    .sort((assignmentA, assignmentB) =>
      assignmentA.sectionName > assignmentB.sectionName ? 1 : -1,
    );

export {
  getAssignmentsForResource,
  getTopicQuestionsFilter,
  getMetadataObjectHirerachyLabels,
  detectUnlockedStatus,
  detectAvailableProgress,
  renderAssessmentProgress,
};
